import React, { useState } from 'react';
import {
  Box,
  Button,
  ButtonProps,
  Collapse,
  Stack,
  Typography,
  TypographyProps,
} from '@mui/material';
import moment from 'moment';

import {
  StyledTableCell,
  StyledTableRow,
} from '../../../../../components/V2/Table/type1/TableStyles';
import {
  getSubmissionsV2State,
  ISubTopicSubmission,
} from '../../../../../store/V2/SubmissionsSlice';
import { TP_COLORS } from '../../../../../constants';
import { msToMinutesSeconds } from '../../../../../helper';
import { useAppSelector } from '../../../../../hooks';
import SubmissionItem from './SubmissionItem';
import {
  getScoreColor,
  getScoreStatus,
} from '../../../QuizPerformance/DetailedOverview/components/StudentPerformanceRow';

const typoProps: TypographyProps = {
  fontSize: '14px',
  color: TP_COLORS.gray.darken,
};

const buttonProps: ButtonProps = {
  disableRipple: true,
  variant: 'text',
  sx: {
    textTransform: 'none',
    textDecoration: 'underline',
    color: TP_COLORS.gray.darken,
    minWidth: 0,
    padding: 0,
    '&:hover': {
      backgroundColor: 'transparent',
      textDecoration: 'underline',
    },
  },
};

interface ISubTopicRowProps {
  rowData: ISubTopicSubmission;
  topicId: string;
}

const SubTopicRow: React.FC<ISubTopicRowProps> = ({ rowData, topicId }) => {
  // State
  const [open, setOpen] = useState(false);

  // Store
  const { studentSubmissionsData } = useAppSelector(getSubmissionsV2State);

  const submissionCount = rowData.submissions.length;

  const latestSubmissionAt = moment(
    rowData.submissions[rowData.submissions.length - 1].completedAt
  );

  const studentTopicAverageScore =
    studentSubmissionsData?.groupedSubmissions.find(
      (item) => item.topicId === topicId
    )?.averageScore || 0;

  return (
    <React.Fragment key={rowData.subTopicId}>
      <StyledTableRow hover>
        <StyledTableCell>
          <Typography {...typoProps}>{rowData.subTopicName || ''}</Typography>
        </StyledTableCell>
        <StyledTableCell>
          <Stack>
            <Typography {...typoProps}>
              {latestSubmissionAt.format('ddd MMM DD YYYY')}
            </Typography>
            <Typography fontSize="12px" color="#94A3B8">
              {latestSubmissionAt.format('hh:mm A')}
            </Typography>
          </Stack>
        </StyledTableCell>
        <StyledTableCell>
          <Stack>
            <Typography {...typoProps}>{rowData.averageScore}/20</Typography>
            <Typography
              fontSize="12px"
              color={getScoreColor(
                rowData.averageScore,
                studentTopicAverageScore
              )}
            >
              {getScoreStatus(rowData.averageScore, studentTopicAverageScore)}
            </Typography>
          </Stack>
        </StyledTableCell>
        <StyledTableCell>
          <Stack>
            <Typography {...typoProps}>Time Taken</Typography>
            <Typography fontSize="12px" color="#94A3B8">
              {msToMinutesSeconds(rowData.averageTime)}
            </Typography>
          </Stack>
        </StyledTableCell>

        <StyledTableCell>
          <Box display="flex" gap="10px">
            <Button {...buttonProps} onClick={() => setOpen(!open)}>
              {open
                ? 'Hide'
                : `${submissionCount} Attempt${submissionCount > 1 ? 's' : ''}`}
            </Button>
          </Box>
        </StyledTableCell>
      </StyledTableRow>
      <StyledTableRow>
        <StyledTableCell
          style={{
            padding: '0px 20px',
            backgroundColor: '#F1F5F9',
          }}
          colSpan={6}
        >
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Stack gap="4px" margin="16px 0px">
              {rowData.submissions.map((topicRow, index) => {
                const isFirst = index === 0;
                const isLast = index === rowData.submissions.length - 1;
                return (
                  <Box
                    key={index}
                    sx={{
                      backgroundColor: TP_COLORS.white,
                      padding: '16px 20px',
                      borderTopLeftRadius: isFirst ? '10px' : 0,
                      borderTopRightRadius: isFirst ? '10px' : 0,
                      borderBottomLeftRadius: isLast ? '10px' : 0,
                      borderBottomRightRadius: isLast ? '10px' : 0,
                      overflow: 'hidden', // prevents overflow clipping when radius applied
                    }}
                  >
                    <SubmissionItem subTopicData={rowData} index={index} />
                  </Box>
                );
              })}
            </Stack>
          </Collapse>
        </StyledTableCell>
      </StyledTableRow>
    </React.Fragment>
  );
};

export default SubTopicRow;
